/* eslint-disable react-compiler/react-compiler */
"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { useEffect, useState } from "react"
import type { UseFormRegister } from "react-hook-form"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  getAccountDetails,
  type UpdateAccountDetailsInput,
  updateAccountDetails,
} from "@/api/account-details"
import { AccountDetailsFormSkeleton } from "@/components/account/details/form/skeleton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

function RequiredMark() {
  return <span className='text-destructive'>*</span>
}

const EMPTY_FORM_VALUES: UpdateAccountDetailsInput = {
  firstName: "",
  lastName: "",
  displayName: "",
  email: "",
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
}

type PasswordFieldName =
  | "currentPassword"
  | "newPassword"
  | "confirmNewPassword"

function PasswordField({
  fieldName,
  id,
  label,
  register,
}: {
  fieldName: PasswordFieldName
  id: string
  label: string
  register: UseFormRegister<UpdateAccountDetailsInput>
}) {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div className='space-y-2'>
      <Label htmlFor={id}>{label}</Label>
      <div className='relative'>
        <Input
          className='h-11 pr-10'
          id={id}
          type={isVisible ? "text" : "password"}
          {...register(fieldName)}
        />
        <button
          aria-label={isVisible ? "Ocultar senha" : "Mostrar senha"}
          className='absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground'
          onClick={() => {
            setIsVisible((previousState) => !previousState)
          }}
          type='button'
        >
          {isVisible ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
        </button>
      </div>
    </div>
  )
}

export default function MinhaContaDetalhesDaContaPage() {
  const queryClient = useQueryClient()
  const form = useForm<UpdateAccountDetailsInput>({
    defaultValues: EMPTY_FORM_VALUES,
    mode: "onSubmit",
  })

  const accountDetailsQuery = useQuery({
    queryKey: ["account", "details"],
    queryFn: getAccountDetails,
    retry: false,
  })

  useEffect(() => {
    if (!accountDetailsQuery.data) {
      return
    }

    if (accountDetailsQuery.data.ok && accountDetailsQuery.data.details) {
      form.reset({
        ...accountDetailsQuery.data.details,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      })

      return
    }

    toast.error(
      accountDetailsQuery.data.message?.trim() ||
        "Nao foi possivel carregar os detalhes da conta."
    )
  }, [accountDetailsQuery.data, form])

  useEffect(() => {
    if (!accountDetailsQuery.isError) {
      return
    }

    toast.error("Nao foi possivel carregar os detalhes da conta.")
  }, [accountDetailsQuery.isError])

  const updateMutation = useMutation({
    mutationFn: updateAccountDetails,
    onMutate: () => {
      const toastId = toast.loading("Salvando alteracoes...")

      return { toastId }
    },
    onError: (error, _values, context) => {
      if (axios.isAxiosError(error)) {
        const payload = error.response?.data as
          | {
              message?: string
              errors?: string[]
            }
          | undefined
        const firstError = payload?.errors?.[0]

        toast.error(
          payload?.message?.trim() ||
            firstError?.trim() ||
            "Nao foi possivel atualizar detalhes da conta.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      toast.error("Nao foi possivel atualizar detalhes da conta.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _values, context) => {
      if (response.details) {
        form.reset({
          ...response.details,
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        })
      } else {
        form.reset({
          ...form.getValues(),
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        })
      }

      toast.success(
        response.message?.trim() ||
          "Detalhes da conta atualizados com sucesso.",
        {
          id: context?.toastId,
        }
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["account", "details"] })
    },
  })

  const onSubmit = (values: UpdateAccountDetailsInput) => {
    updateMutation.mutate(values)
  }

  const isFormDisabled = updateMutation.isPending
  let submitLabel = "Salvar alteracoes"

  if (updateMutation.isPending) {
    submitLabel = "Salvando..."
  }

  if (accountDetailsQuery.isPending) {
    return <AccountDetailsFormSkeleton />
  }

  return (
    <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
      <div className='max-w-3xl space-y-8'>
        <h1 className='font-semibold text-3xl'>Detalhes da conta</h1>

        <form className='space-y-8' onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset className='space-y-8' disabled={isFormDisabled}>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='first-name'>
                  Nome <RequiredMark />
                </Label>
                <Input
                  className='h-11'
                  id='first-name'
                  {...form.register("firstName")}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='last-name'>
                  Sobrenome <RequiredMark />
                </Label>
                <Input
                  className='h-11'
                  id='last-name'
                  {...form.register("lastName")}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='display-name'>
                  Nome de exibicao <RequiredMark />
                </Label>
                <Input
                  className='h-11'
                  id='display-name'
                  {...form.register("displayName")}
                />
                <p className='text-muted-foreground text-sm italic'>
                  Sera assim que seu nome sera exibido na secao da conta e nos
                  comentarios
                </p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='email'>
                  Endereco de e-mail <RequiredMark />
                </Label>
                <Input
                  className='h-11'
                  id='email'
                  type='email'
                  {...form.register("email")}
                />
              </div>
            </div>

            <div className='space-y-4'>
              <h2 className='font-semibold text-2xl'>Alteracao de senha</h2>
              <PasswordField
                fieldName='currentPassword'
                id='current-password'
                label='Senha atual (deixe em branco para nao alterar)'
                register={form.register}
              />
              <PasswordField
                fieldName='newPassword'
                id='new-password'
                label='Nova senha (deixe em branco para nao alterar)'
                register={form.register}
              />
              <PasswordField
                fieldName='confirmNewPassword'
                id='confirm-password'
                label='Confirmar nova senha'
                register={form.register}
              />
            </div>

            <Button className='h-12 px-8 text-base' type='submit'>
              {submitLabel}
            </Button>
          </fieldset>
        </form>
      </div>
    </section>
  )
}
